import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import type {
  Organization,
  OrganizationInsert,
  OrganizationUpdate,
  OrganizationDomain,
  OrganizationMember,
  SsoStatistics
} from '../types/database'

interface OrganizationWithDetails extends Organization {
  domains?: OrganizationDomain[]
  members_count?: number
  sso_connections_count?: number
}

interface UseOrganizationsReturn {
  organizations: OrganizationWithDetails[]
  loading: boolean
  error: string | null
  createOrganization: (data: OrganizationInsert) => Promise<Organization | null>
  updateOrganization: (id: string, data: OrganizationUpdate) => Promise<boolean>
  deleteOrganization: (id: string) => Promise<boolean>
  getOrganization: (id: string) => Promise<OrganizationWithDetails | null>
  // Domain management
  addDomain: (organizationId: string, domain: string) => Promise<OrganizationDomain | null>
  removeDomain: (domainId: string) => Promise<boolean>
  verifyDomain: (domainId: string) => Promise<boolean>
  getDomains: (organizationId: string) => Promise<OrganizationDomain[]>
  // Members management
  getMembers: (organizationId: string) => Promise<OrganizationMember[]>
  removeMember: (memberId: string) => Promise<boolean>
  updateMemberRole: (memberId: string, role: 'admin' | 'member') => Promise<boolean>
  // Statistics
  getStatistics: () => Promise<SsoStatistics | null>
  refresh: () => Promise<void>
}

export function useOrganizations(): UseOrganizationsReturn {
  const { user } = useAuth()
  const [organizations, setOrganizations] = useState<OrganizationWithDetails[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadOrganizations = useCallback(async () => {
    if (!user) {
      setOrganizations([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      // Load organizations with domain count and member count
      const { data, error: fetchError } = await supabase
        .from('organizations')
        .select(`
          *,
          organization_domains(count),
          organization_members(count),
          sso_connections(count)
        `)
        .order('created_at', { ascending: false })

      if (fetchError) throw fetchError

      // Transform data to include counts
      const orgsWithDetails = (data || []).map(org => ({
        ...org,
        domains_count: org.organization_domains?.[0]?.count || 0,
        members_count: org.organization_members?.[0]?.count || 0,
        sso_connections_count: org.sso_connections?.[0]?.count || 0
      }))

      setOrganizations(orgsWithDetails)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load organizations')
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    loadOrganizations()
  }, [loadOrganizations])

  const createOrganization = async (data: OrganizationInsert): Promise<Organization | null> => {
    if (!user) return null

    try {
      // Use the admin RPC function
      const { data: orgId, error: createError } = await supabase.rpc('admin_create_organization', {
        p_name: data.name,
        p_slug: data.slug || null,
        p_logo_url: data.logo_url || null
      })

      if (createError) throw createError

      // Fetch the created organization
      const { data: created, error: fetchError } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', orgId)
        .single()

      if (fetchError) throw fetchError

      setOrganizations(prev => [created, ...prev])
      return created
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create organization')
      return null
    }
  }

  const updateOrganization = async (id: string, data: OrganizationUpdate): Promise<boolean> => {
    try {
      const { error: updateError } = await supabase
        .from('organizations')
        .update(data)
        .eq('id', id)

      if (updateError) throw updateError

      setOrganizations(prev =>
        prev.map(org => org.id === id ? { ...org, ...data } : org)
      )
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update organization')
      return false
    }
  }

  const deleteOrganization = async (id: string): Promise<boolean> => {
    try {
      const { error: deleteError } = await supabase
        .from('organizations')
        .delete()
        .eq('id', id)

      if (deleteError) throw deleteError

      setOrganizations(prev => prev.filter(org => org.id !== id))
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete organization')
      return false
    }
  }

  const getOrganization = async (id: string): Promise<OrganizationWithDetails | null> => {
    try {
      const { data, error: fetchError } = await supabase
        .from('organizations')
        .select(`
          *,
          organization_domains(*),
          organization_members(count),
          sso_connections(count)
        `)
        .eq('id', id)
        .single()

      if (fetchError) throw fetchError

      return {
        ...data,
        domains: data.organization_domains || [],
        members_count: data.organization_members?.[0]?.count || 0,
        sso_connections_count: data.sso_connections?.[0]?.count || 0
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get organization')
      return null
    }
  }

  // Domain management
  const addDomain = async (organizationId: string, domain: string): Promise<OrganizationDomain | null> => {
    try {
      const { data: domainId, error: addError } = await supabase.rpc('admin_add_organization_domain', {
        p_organization_id: organizationId,
        p_domain: domain
      })

      if (addError) throw addError

      // Fetch the created domain
      const { data: created, error: fetchError } = await supabase
        .from('organization_domains')
        .select('*')
        .eq('id', domainId)
        .single()

      if (fetchError) throw fetchError

      return created
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add domain')
      return null
    }
  }

  const removeDomain = async (domainId: string): Promise<boolean> => {
    try {
      const { error: deleteError } = await supabase
        .from('organization_domains')
        .delete()
        .eq('id', domainId)

      if (deleteError) throw deleteError

      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove domain')
      return false
    }
  }

  const verifyDomain = async (domainId: string): Promise<boolean> => {
    try {
      const { error: verifyError } = await supabase.rpc('admin_verify_organization_domain', {
        p_domain_id: domainId
      })

      if (verifyError) throw verifyError

      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify domain')
      return false
    }
  }

  const getDomains = async (organizationId: string): Promise<OrganizationDomain[]> => {
    try {
      const { data, error: fetchError } = await supabase
        .from('organization_domains')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })

      if (fetchError) throw fetchError

      return data || []
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get domains')
      return []
    }
  }

  // Members management
  const getMembers = async (organizationId: string): Promise<OrganizationMember[]> => {
    try {
      const { data, error: fetchError } = await supabase
        .from('organization_members')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })

      if (fetchError) throw fetchError

      return data || []
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get members')
      return []
    }
  }

  const removeMember = async (memberId: string): Promise<boolean> => {
    try {
      const { error: deleteError } = await supabase
        .from('organization_members')
        .delete()
        .eq('id', memberId)

      if (deleteError) throw deleteError

      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove member')
      return false
    }
  }

  const updateMemberRole = async (memberId: string, role: 'admin' | 'member'): Promise<boolean> => {
    try {
      const { error: updateError } = await supabase
        .from('organization_members')
        .update({ role })
        .eq('id', memberId)

      if (updateError) throw updateError

      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update member role')
      return false
    }
  }

  // Statistics
  const getStatistics = async (): Promise<SsoStatistics | null> => {
    try {
      const { data, error: fetchError } = await supabase.rpc('get_sso_statistics')

      if (fetchError) throw fetchError

      return data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get statistics')
      return null
    }
  }

  return {
    organizations,
    loading,
    error,
    createOrganization,
    updateOrganization,
    deleteOrganization,
    getOrganization,
    addDomain,
    removeDomain,
    verifyDomain,
    getDomains,
    getMembers,
    removeMember,
    updateMemberRole,
    getStatistics,
    refresh: loadOrganizations
  }
}
